import { api } from './api';
import { UploadResponse } from '../types/kyc';

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export interface UploadError {
  message: string;
  code: 'INVALID_TYPE' | 'FILE_TOO_LARGE' | 'UPLOAD_FAILED';
}

export const uploadService = {
  /**
   * Validate file before upload
   */
  validateFile(file: File): UploadError | null {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return {
        message: 'Invalid file type. Allowed: PDF, JPG, PNG, GIF, WEBP',
        code: 'INVALID_TYPE',
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      return {
        message: 'File too large. Maximum size is 10MB',
        code: 'FILE_TOO_LARGE',
      };
    }

    return null;
  },

  /**
   * Upload file to backend
   * @param file - File to upload
   * @returns Upload response with file_url
   */
  async uploadFile(file: File): Promise<UploadResponse> {
    // Validate file first
    const validationError = this.validateFile(file);
    if (validationError) {
      throw new Error(validationError.message);
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<UploadResponse>('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  /**
   * Get file URL for viewing/downloading
   * @param filename - Filename from upload response
   * @returns Full URL to file
   */
  getFileUrl(fileUrl: string): string {
    // If it's already a full URL, return it
    if (fileUrl.startsWith('http')) {
      return fileUrl;
    }
    // Otherwise, prepend the API base URL
    const baseUrl = api.defaults.baseURL || '';
    return `${baseUrl}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
  },

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  },
};

export default uploadService;
